/**
 * The project map's Gantt view (Work → Map, "Gantt"): the workbench Roadmap's Timeline layout
 * (./RoadmapGantt.tsx) on the console's data. One roadmap at a time — the project's own, or one
 * repository's as its workbench mirrors it (`GET /projects/v1/:id/map`) — with the runs that served
 * it (`GET /projects/v1/:id/runs`, the last 90 days):
 *
 *   What's ahead   — the dated roadmap: finished work from its runs, work under way, a person's
 *                    target, and where there is none a forecast from the recent pace;
 *   What happened  — the work timeline of the runs that served this roadmap.
 */
import { effectiveRoadmapStatus, PROJECT_LEVEL_ROOT_ID, type ProjectMapResponse } from '@aioe/shared';
import { useMemo, useState } from 'react';

import { ChoiceSelect, ListBlock, ListGroup } from '../ui.tsx';
import { TimelinePanel } from '../timeline/TimelinePanel.tsx';
import { TimelineOpenProvider } from '../timeline/timelineOpen.tsx';
import type { TimelineRun } from '../timeline/workTimelineModel.ts';
import type { RoadmapRowLike } from './roadmapGantt.ts';
import { RoadmapTimelineArea } from './RoadmapGantt.tsx';
import { useGanttChoices, useRoadmapGantt } from './useRoadmapGantt.ts';

/** The ListBox id for the project's own roadmap: an empty string is not a reliable id. */
const PROJECT = '__project';

export function ProjectGantt({ projectId, map, onOpen }: { projectId: string; map: ProjectMapResponse; tone: 'green'; onOpen?: (item: { rootId: string; id: string }) => void }) {
  const projectRows = useMemo(() => map.project?.roadmap?.rows ?? [], [map]);
  const roots = map.roots.filter((r) => r.roadmap?.length);
  const choices: Record<string, string> = {
    ...(projectRows.length ? { [PROJECT]: 'Project roadmap' } : {}),
    ...Object.fromEntries(roots.map((r) => [r.rootId, r.rootId])),
  };
  const [picked, setPicked] = useState<string>();
  const which = picked && picked in choices ? picked : (Object.keys(choices)[0] ?? PROJECT);
  const level = which === PROJECT ? 'project' : 'repository';
  const rootId = which === PROJECT ? PROJECT_LEVEL_ROOT_ID : which;

  const rows = useMemo<RoadmapRowLike[]>(() => {
    if (which === PROJECT)
      return projectRows.map((r) => {
        // A row rolled up from its delegates is what they add up to; "blocked" is still under way on a date scale.
        const shown = effectiveRoadmapStatus(r);
        return { id: r.id, title: r.title, area: r.area ?? '', phase: r.phase, status: shown === 'blocked' ? 'in-progress' : shown, ...(r.dependsOn?.length ? { dependsOn: r.dependsOn } : {}), ...(r.target ? { target: r.target } : {}) };
      });
    const root = map.roots.find((r) => r.rootId === which);
    return (root?.roadmap ?? []).map((r) => {
      const target = (r as { target?: unknown }).target;
      return { id: r.id, title: r.title, area: r.area, phase: r.phase, status: r.status, ...(r.dependsOn?.length ? { dependsOn: r.dependsOn } : {}), ...(typeof target === 'string' && target ? { target } : {}) };
    });
  }, [which, projectRows, map.roots]);

  const choice = useGanttChoices(level);
  const gantt = useRoadmapGantt(rows, { level, projectId, rootId, lanes: choice.lanes, enabled: rows.length > 0 });
  // What happened: the runs whose roadmap links name a row of this roadmap.
  const ids = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);
  const served = useMemo(() => (run: TimelineRun) => (run.roadmap ?? []).some((ref) => ref.rootId === rootId && ids.has(ref.id)), [rootId, ids]);

  return (
    <ListGroup title="Roadmap on dates" count={rows.length || undefined} description="Finished work from its runs, targets people set, and a forecast from the recent pace where there is none.">
      <ListBlock>
      <TimelineOpenProvider value={{ projectId }}>
        <RoadmapTimelineArea
          mode={choice.mode}
          onMode={choice.setMode}
          lanes={choice.lanes}
          onLanes={choice.setLanes}
          extra={Object.keys(choices).length > 1 ? <ChoiceSelect label="Roadmap" className="w-48" value={which} options={choices} onChange={setPicked} /> : undefined}
          gantt={gantt}
          {...(onOpen ? { onOpen: (id: string) => onOpen({ rootId, id }) } : {})}
          happened={<TimelinePanel scope="project" projectId={projectId} match={served} chips smallStats rangeView="roadmap-runs" defaultPreset="30d" filterLabel={which === PROJECT ? 'Runs on the project roadmap' : `Runs on the ${which} roadmap`} emptyText="No run in this range served an item on this roadmap. A run serves an item when its task or plan names it." />}
        />
      </TimelineOpenProvider>
      </ListBlock>
    </ListGroup>
  );
}
